import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { useNavigate } from 'react-router-dom'
import { C } from '../landing.theme'
import { Ic } from '../ui/Ic'
import { Fade } from '../ui/Fade'
import { navigateToNewProject } from '../../shared/navigation/navigateToNewProject'
import { markNewProjectOnboardingIntent } from '../../features/onboarding'
import { ConsultModal } from '../components/ConsultModal'

const LandingPoolPreview = lazy(() =>
  import('../LandingPoolPreview').then(m => ({ default: m.LandingPoolPreview }))
)

/* ── Count-up hook ── */
function useCountUp(target, duration = 1400) {
  const [count, setCount] = useState(0)
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ob = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return
      ob.disconnect()
      const start = performance.now()
      const tick = (now) => {
        const p = Math.min((now - start) / duration, 1)
        const eased = 1 - Math.pow(1 - p, 3)
        setCount(Math.round(eased * target))
        if (p < 1) requestAnimationFrame(tick)
      }
      requestAnimationFrame(tick)
    }, { threshold: 0.4 })
    ob.observe(el)
    return () => ob.disconnect()
  }, [target, duration])
  return [count, ref]
}

// 22 пузырька, position:absolute (не fixed) — не нагружают скролл
const BUBBLES = [
  { left:'3%',  s:4,  delay:0,   dur:11, dr:3.8 },
  { left:'8%',  s:6,  delay:1.8, dur:13, dr:4.2 },
  { left:'13%', s:3,  delay:3.5, dur:9,  dr:3.1 },
  { left:'17%', s:7,  delay:0.4, dur:12, dr:5.0 },
  { left:'22%', s:4,  delay:5.2, dur:10, dr:3.6 },
  { left:'27%', s:5,  delay:2.1, dur:14, dr:4.8 },
  { left:'31%', s:3,  delay:1.0, dur:9,  dr:3.3 },
  { left:'36%', s:4,  delay:4.0, dur:11, dr:4.0 },
  { left:'40%', s:8,  delay:0.7, dur:10, dr:5.5 },
  { left:'45%', s:4,  delay:3.2, dur:13, dr:3.9 },
  { left:'50%', s:3,  delay:6.0, dur:9,  dr:3.2 },
  { left:'54%', s:6,  delay:1.4, dur:12, dr:4.5 },
  { left:'59%', s:4,  delay:2.6, dur:11, dr:3.7 },
  { left:'63%', s:4,  delay:0.2, dur:10, dr:4.1 },
  { left:'68%', s:3,  delay:4.7, dur:14, dr:3.5 },
  { left:'72%', s:7,  delay:1.1, dur:11, dr:5.2 },
  { left:'76%', s:4,  delay:3.8, dur:10, dr:3.8 },
  { left:'80%', s:5,  delay:2.3, dur:13, dr:4.6 },
  { left:'84%', s:3,  delay:5.5, dur:9,  dr:3.0 },
  { left:'88%', s:4,  delay:0.9, dur:12, dr:4.3 },
  { left:'92%', s:3,  delay:3.0, dur:11, dr:3.6 },
  { left:'96%', s:3,  delay:1.6, dur:10, dr:4.0 },
]

function Bubbles() {
  return (
    // contain: изолирует перерисовку пузырьков внутри Hero — браузер не пересчитывает layout снаружи
    <div style={{ position:'absolute', inset:0, overflow:'hidden', pointerEvents:'none', zIndex:10, contain:'layout style paint' }}>
      {BUBBLES.map((b,i) => {
        const op = 0.22 + (i % 4) * 0.07
        return (
          <div key={i} style={{
            position:'absolute', bottom:-20, left:b.left,
            animation:`bubbleFloat ${b.dur}s ${b.delay}s ease-in infinite`,
          }}>
            <div style={{
              width:b.s, height:b.s, borderRadius:'50%',
              border:`1px solid rgba(31,200,214,${op + 0.12})`,
              background:`radial-gradient(circle at 30% 30%, rgba(255,255,255,${op * 0.3}), rgba(31,200,214,${op * 0.65}) 40%, transparent)`,
              animation:`bubbleDrift ${b.dr}s ${b.delay * 0.4}s ease-in-out infinite`,
            }}/>
          </div>
        )
      })}
    </div>
  )
}

const EQL = [
  { id: 'filter', n: 'filter', name: 'Фильтрация',   sub: 'Песочный фильтр + насос' },
  { id: 'heat',   n: 'heat',   name: 'Подогрев',     sub: 'Теплообменник/Электронагреватель' },
  { id: 'desin',  n: 'desin',  name: 'Дезинфекция',  sub: 'Хлоринатор/Ультрафиолет' },
  { id: 'light',  n: 'light',  name: 'Прожектор',    sub: 'RGB LED' },
  { id: 'ladder', n: 'ladder', name: 'Лестница',     sub: 'AISI-304/316' },
  { id: 'embed',  n: 'shield', name: 'Закладные',    sub: 'Скиммеры, форсунки, трап' },
]

const FNL = [
  { id: 'pvh',    name: 'ПВХ плёнка', hex: '#5B8A9F', pictures: [null, '/texture/pvh-film.webp'],  type: 'pvc-film' },
  { id: 'mosaic', name: 'Мозаика',    hex: '#0FA3B1', pictures: [null, '/texture/mosaic.webp'],   type: 'mosaic' },
]

function RangeSlider({ label, value, min, max, step, onChangeFn }) {
  const pct = ((value - min) / (max - min)) * 100
  const dec = step < 1 ? 1 : 0
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: C.s3, fontFamily: "'JetBrains Mono',monospace", letterSpacing: '.14em', textTransform: 'uppercase' }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: C.tealBright, fontFamily: "'JetBrains Mono',monospace" }}>{value.toFixed(dec)} м</span>
      </div>
      <div style={{ position: 'relative', height: 24, display: 'flex', alignItems: 'center' }}>
        {/* Track */}
        <div style={{ position: 'absolute', left: 0, right: 0, height: 4, borderRadius: 4, background: 'rgba(255,255,255,.07)', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: 'linear-gradient(90deg,#0A7B86,#1FC8D6)', boxShadow: '0 0 8px rgba(31,200,214,.4)' }}/>
        </div>
        {/* Thumb */}
        <div style={{
          position: 'absolute', left: `${pct}%`, transform: 'translateX(-50%)',
          width: 20, height: 20, borderRadius: '50%', pointerEvents: 'none',
          background: 'linear-gradient(145deg,#2DDDE8 0%,#0FA3B1 55%,#0A7B86 100%)',
          boxShadow: '0 0 0 3px rgba(31,200,214,.2), 0 2px 10px rgba(0,0,0,.55), inset 0 1px 0 rgba(255,255,255,.4)',
        }}/>
        {/* Invisible native input — handles all interaction */}
        <input type="range" min={min} max={max} step={step} value={value}
          aria-label={`${label}: ${value.toFixed(step < 1 ? 1 : 0)} м`}
          onChange={e => onChangeFn(parseFloat(e.target.value))}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', margin: 0, padding: 0, WebkitAppearance: 'none', appearance: 'none' }}
        />
      </div>
    </div>
  )
}

export default function Hero() {
  const [len, setLen] = useState(6)
  const [wid, setWid] = useState(4)
  const [dep, setDep] = useState(1.6)
  const [eq,  setEq]  = useState({ filter: true, heat: true, desin: true, light: false, ladder: false, embed: false })
  const [fin, setFin] = useState('pvh')
  const [modal, setModal] = useState(false)
  const navigate = useNavigate()

  // Оборудование появляется быстро — все 3 до 800ms, до начала скролла
  useEffect(() => {
    const seq = [
      { key: 'light',  delay: 300 },
      { key: 'ladder', delay: 550 },
      { key: 'embed',  delay: 800 },
    ]
    const timers = seq.map(({ key, delay }) =>
      setTimeout(() => setEq(prev => ({ ...prev, [key]: true })), delay)
    )
    return () => timers.forEach(clearTimeout)
  }, [])

  const [posCount, posRef] = useCountUp(500)

  const finO = FNL.find(f => f.id === fin)
  const tog  = id => setEq(p => ({ ...p, [id]: !p[id] }))

  return (
    <section id="top" className="hero-sec">
      <Bubbles/>
      <div style={{ position: 'absolute', top: '-18%', right: '-6%', width: '58%', height: '82%', background: 'radial-gradient(circle,rgba(15,163,177,.18),transparent 62%)', pointerEvents: 'none' }}/>
      <div style={{ position: 'absolute', bottom: '-10%', left: '-8%', width: '50%', height: '60%', background: 'radial-gradient(circle,rgba(15,163,177,.07),transparent 60%)', pointerEvents: 'none' }}/>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(31,200,214,.044) 1px,transparent 1px),linear-gradient(90deg,rgba(31,200,214,.044) 1px,transparent 1px)', backgroundSize: '44px 44px', WebkitMaskImage: 'radial-gradient(85% 70% at 28% 22%,#000,transparent 76%)', maskImage: 'radial-gradient(85% 70% at 28% 22%,#000,transparent 76%)', pointerEvents: 'none' }}/>

      <div className="wrap-hero">
        <div className="hero-inner">
          <div className="hero-3col">

            {/* ─── Left: copy ─── */}
            <Fade className="hero-copy-wrap">
              <div className="hero-copy-inner" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span className="eb hero-badge" style={{ fontSize: 11 }}>3D ПОДБОР ОБОРУДОВАНИЯ ДЛЯ БАССЕЙНОВ</span>
                <h1 style={{ fontSize: 'clamp(30px,3.4vw,52px)', lineHeight: 1.08, color: '#fff', margin: '18px 0 0', letterSpacing: '-.035em', fontWeight: 900 }}>
                  Подберите<br/>
                  <span className="chrome-bright" style={{ letterSpacing: '-.025em' }}>оборудование</span><br/>
                  для вашего<br/>бассейна
                </h1>
                <p style={{ color: C.s2, fontSize: 'clamp(13px,1.2vw,15px)', lineHeight: 1.68, maxWidth: 360, marginTop: 16 }}>
                  Welland Create объединяет точные инженерные расчёты и 3D‑визуализацию. Введите размеры чаши, выберите оборудование - и за минуты получите готовый проект, спецификацию с ценами и коммерческое предложение.
                </p>
                <div className="hero-btns" style={{ display: 'flex', gap: 10, marginTop: 24, flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-p"
                    style={{ padding: '15px 28px', fontSize: 15, boxShadow: '0 8px 28px rgba(15,163,177,.38),inset 0 1px 0 rgba(255,255,255,.24)' }}
                    onClick={() => navigateToNewProject(navigate)}
                  >
                    <Ic n="cube" s={17}/>Начать подбор
                  </button>
                  <button className="btn btn-g" style={{ padding: '14px 20px', fontSize: 14 }} onClick={() => setModal(true)}>
                    <Ic n="phone" s={15}/>Консультация
                  </button>
                </div>

                {/* Stats with count-up */}
                <div className="hero-stats" style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 30, flexWrap: 'wrap' }}>
                  {/* Count-up: 500+ позиций */}
                  <div ref={posRef}>
                    <b className="stat-num">{posCount}+</b>
                    <span style={{ fontSize: 11, color: C.s3, marginTop: 1, display: 'block' }}>позиций</span>
                  </div>
                  <div style={{ width: 1, height: 28, background: C.line2, flexShrink: 0 }}/>
                  <div>
                    <b className="stat-num">Бесплатный</b>
                    <span style={{ fontSize: 11, color: C.s3, marginTop: 1, display: 'block' }}>подбор</span>
                  </div>
                  <div style={{ width: 1, height: 28, background: C.line2, flexShrink: 0 }}/>
                  <div>
                    <b className="stat-num">с 2019</b>
                    <span style={{ fontSize: 11, color: C.s3, marginTop: 1, display: 'block' }}>на рынке</span>
                  </div>
                </div>
              </div>
            </Fade>

            {/* ─── Center: 3D model ─── */}
            <Fade delay={80} y={24}>
              <div className="hero-col-card" style={{ borderRadius: 18, overflow: 'hidden', border: `1px solid ${C.line}`, boxShadow: '0 28px 70px rgba(0,0,0,.55)' }}>
                <div style={{ background: C.bg0, backgroundImage: `linear-gradient(rgba(31,200,214,.048) 1px,transparent 1px),linear-gradient(90deg,rgba(31,200,214,.048) 1px,transparent 1px)`, backgroundSize: '34px 34px', padding: '10px 10px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '.18em', color: C.tealBright }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.tealBright, animation: 'ringAnim 2s infinite' }}/>3D · LIVE
                    </span>
                    <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: C.s3 }}>{len.toFixed(1)}×{wid.toFixed(1)}×{dep.toFixed(1)} м</span>
                  </div>
                  <div className="hero-canvas-wrap">
                    <Suspense fallback={
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'radial-gradient(ellipse at 50% 60%, rgba(15,163,177,.08), transparent 70%)' }}>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '.2em', color: 'rgba(15,163,177,.5)' }}>3D · ЗАГРУЗКА…</span>
                      </div>
                    }>
                      <LandingPoolPreview
                        len={len} wid={wid} dep={dep} finObj={finO} eq={eq}
                        figDescription={`Интерактивная 3D модель бассейна ${len.toFixed(1)}×${wid.toFixed(1)}×${dep.toFixed(1)} м - Welland Create, 3D моделирование и подбор оборудования для бассейна онлайн. Насосы, фильтры, теплообменники, прожекторы, лестница, закладные элементы. Казахстан, Алматы.`}
                      />
                    </Suspense>
                    <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 5, pointerEvents: 'none', opacity: .45 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1FC8D6" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M8 12l4-4 4 4M8 16h8"/></svg>
                      <span style={{ fontSize: 9, fontFamily: "'JetBrains Mono',monospace", letterSpacing: '.14em', color: '#1FC8D6' }}>ПОТЯНИТЕ ДЛЯ ВРАЩЕНИЯ</span>
                    </div>
                  </div>
                </div>
                <div style={{ padding: '16px 16px 18px', background: C.bg2, borderTop: `1px solid ${C.line}`, display: 'flex', flexDirection: 'column', gap: 14, flex: 1, justifyContent: 'center' }}>
                  <p style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.22em', color: C.s3, textTransform: 'uppercase', textAlign: 'center', margin: 0 }}>— Размеры чаши —</p>
                  <RangeSlider label="Длина"  value={len} min={2}   max={10}  step={0.5} onChangeFn={setLen}/>
                  <RangeSlider label="Ширина" value={wid} min={2}   max={10}  step={0.5} onChangeFn={setWid}/>
                  <RangeSlider label="Глубина" value={dep} min={1.2} max={2.4} step={0.1} onChangeFn={setDep}/>
                </div>
              </div>
            </Fade>

            {/* ─── Right: equipment picker ─── */}
            <Fade delay={160} y={24}>
              <div className="hero-col-card" style={{ borderRadius: 18, border: `1px solid ${C.line}`, overflow: 'hidden', background: C.bg1, boxShadow: '0 24px 60px rgba(0,0,0,.45)' }}>
                {/* Finishing */}
                <div style={{ padding: '16px 16px 14px', borderBottom: `1px solid ${C.line}` }}>
                  <p style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.22em', color: C.s3, textTransform: 'uppercase', textAlign: 'center', marginBottom: 10 }}>— Отделка —</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    {FNL.map(f => (
                      <button key={f.id} onClick={() => setFin(f.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, background: fin === f.id ? 'rgba(15,163,177,.13)' : 'rgba(255,255,255,.04)', border: `1px solid ${fin === f.id ? C.teal : C.line}`, borderRadius: 10, padding: '9px 10px', color: fin === f.id ? '#fff' : C.s2, fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all .22s', boxShadow: fin === f.id ? '0 0 0 3px rgba(15,163,177,.12)' : 'none' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: f.hex, flexShrink: 0, boxShadow: fin === f.id ? `0 0 8px ${f.hex}` : 'none' }}/>{f.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Equipment */}
                <div className="hero-eq-body" style={{ padding: '14px 14px 10px', borderBottom: `1px solid ${C.line}` }}>
                  <p style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.22em', color: C.s3, textTransform: 'uppercase', textAlign: 'center', marginBottom: 10 }}>— Оборудование —</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {EQL.map(e => (
                      <button key={e.id} onClick={() => tog(e.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, background: eq[e.id] ? 'rgba(15,163,177,.08)' : 'rgba(255,255,255,.02)', border: `1px solid ${eq[e.id] ? 'rgba(15,163,177,.45)' : C.line}`, borderRadius: 10, padding: '7px 10px', transition: 'all .2s', cursor: 'pointer' }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, background: eq[e.id] ? 'rgba(15,163,177,.18)' : 'rgba(255,255,255,.05)', border: `1px solid ${eq[e.id] ? 'rgba(15,163,177,.3)' : 'rgba(255,255,255,.06)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: eq[e.id] ? C.tealBright : C.s3, transition: 'all .2s' }}>
                          <Ic n={e.n} s={13}/>
                        </div>
                        <span style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: eq[e.id] ? '#fff' : C.s2, lineHeight: 1.25 }}>{e.name}</span>
                          <span style={{ fontSize: 9.5, color: C.s4, fontFamily: "'JetBrains Mono',monospace" }}>{e.sub}</span>
                        </span>
                        <span style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, background: eq[e.id] ? C.teal : 'transparent', border: `1.5px solid ${eq[e.id] ? C.teal : C.s4}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#03252a', transition: 'all .2s' }}>
                          {eq[e.id] && <Ic n="check" s={9}/>}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div style={{ padding: '14px 16px 16px', textAlign: 'center' }}>
                  <button
                    className="btn btn-p"
                    style={{ width: '100%', justifyContent: 'center', fontSize: 13.5, padding: '13px 16px' }}
                    onClick={() => {
                      markNewProjectOnboardingIntent()
                      navigate(`/app?len=${len}&wid=${wid}&dep=${dep}`, { state: { startNewProject: true } })
                    }}
                  >
                    <Ic n="cube" s={15}/>Перейти к проектированию
                  </button>
                  <p style={{ fontSize: 9.5, color: C.s4, textAlign: 'center', marginTop: 8, fontFamily: "'JetBrains Mono',monospace", letterSpacing: '.12em', textTransform: 'uppercase' }}>
                    Бесплатно
                  </p>
                </div>
              </div>
            </Fade>
          </div>
        </div>
      </div>

      <div className="hero-scroll">
        <span style={{ width: 3, height: 52, background: `linear-gradient(${C.tealBright},transparent)`, animation: 'scrollLine 2s infinite', borderRadius: 2 }}/>ЛИСТАЙТЕ ВНИЗ
      </div>

      <ConsultModal open={modal} onClose={() => setModal(false)} subtitle={`${len.toFixed(1)} × ${wid.toFixed(1)} × ${dep.toFixed(1)} м`}/>
    </section>
  )
}
